import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import HeroSection from "@/components/layout/HeroSection";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import SEOHelmet from "@/components/SEOHelmet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Smartphone, Mail, Shield, FileText, Scale, Lock, Download, Award, Building2 } from "lucide-react";
import erstinformationHero from "@/assets/erstinformation-hero.jpg";

const Erstinformation = () => {
  return (
    <>
      <SEOHelmet />
      <Navigation />
      <PageBreadcrumbs items={[
        { label: "Home", path: "/" },
        { label: "Erstinformation" }
      ]} />
      
      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
      </div>

      <div className="min-h-screen flex flex-col relative z-10">
      
      <main className="flex-1">
        {/* Hero Section */}
        <HeroSection
          image={erstinformationHero}
          alt="Versicherungsmakler Erstinformation nach §15 VersVermV - Rechtliche Pflichtinformationen für Kunden"
          title="Versicherungsinformationen nach § 15 VVG – Transparente Beratung zu Cyberversicherungen"
          gradientPreset="none"
          imageOpacity={30}
          overlayColor="background"
          className="py-24 md:py-32 px-4"
        >
          <div className="container mx-auto max-w-4xl text-center relative z-10 animate-fade-in">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-6">
              <Shield className="w-7 h-7 text-accent" />
            </div>
            
            <h1 className="heading-1 mb-6 text-foreground leading-tight">
              Erstinformation
            </h1>
            
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-accent/5 rounded-full text-accent text-sm font-medium">
                § 15 VersVermV • § 12 FinVermV
              </span>
            </div>
            
            <p className="body-text text-foreground/70 max-w-2xl mx-auto">
              Gesetzlich vorgeschriebene Informationen für Versicherungs- und Finanzdienstleistungen
            </p>
          </div>
        </HeroSection>


        {/* Kontakt Section */}
        <ResponsiveSection spacing="lg" containerSize="md">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Kontakt Ihres Maklers</h2>
          </div>
          <Card className="border-accent/20 hover:border-accent/40 transition-colors">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <p className="text-xl font-semibold">Thomas Dewein</p>
                </div>
                <div className="grid gap-4">
                  <a 
                    href="tel:073168231" 
                    className="flex items-center gap-3 p-4 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-accent" />
                    <div>
                      <p className="text-xs text-muted-foreground">Telefon</p>
                      <p className="font-medium">0731/68231</p>
                    </div>
                  </a>
                  <a 
                    href="tel:01636389945" 
                    className="flex items-center gap-3 p-4 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors"
                  >
                    <Smartphone className="w-5 h-5 text-accent" />
                    <div>
                      <p className="text-xs text-muted-foreground">Mobil</p>
                      <p className="font-medium">0163/6389945</p>
                    </div>
                  </a>
                  <a 
                    href="mailto:info@praxis-versichert.de" 
                    className="flex items-center gap-3 p-4 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-accent" />
                    <div>
                      <p className="text-xs text-muted-foreground">E-Mail (Allgemein)</p>
                      <p className="font-medium break-all">info@praxis-versichert.de</p>
                    </div>
                  </a>
                  <a 
                    href="mailto:thomas.dewein@deutsches-maklerforum.de" 
                    className="flex items-center gap-3 p-4 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-accent" />
                    <div>
                      <p className="text-xs text-muted-foreground">E-Mail (Persönlich)</p>
                      <p className="font-medium break-all">thomas.dewein@deutsches-maklerforum.de</p>
                    </div>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </ResponsiveSection>

        {/* IHK Registrierung Section */}
        <ResponsiveSection variant="card" spacing="lg">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Meine IHK-Registrierung</h2>
            <p className="text-muted-foreground">Offiziell registriert und zertifiziert</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card className="border-accent/20">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Award className="w-8 h-8 text-accent" />
                  <Badge variant="default">IHK-Registriert</Badge>
                </div>
                <CardTitle className="text-lg">Versicherungen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-2">Registrierungsnummer:</p>
                <p className="font-mono text-sm bg-accent/10 p-2 rounded break-all">D-BEIE-KKYNN-52</p>
              </CardContent>
            </Card>

            <Card className="border-accent/20">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Award className="w-8 h-8 text-accent" />
                  <Badge variant="default">IHK-Registriert</Badge>
                </div>
                <CardTitle className="text-lg">Finanzanlagen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-2">Registrierungsnummer:</p>
                <p className="font-mono text-sm bg-accent/10 p-2 rounded break-all">D-F-155-F6HW-78</p>
              </CardContent>
            </Card>

            <Card className="border-accent/20 md:col-span-2 lg:col-span-1">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Award className="w-8 h-8 text-accent" />
                  <Badge variant="default">IHK-Registriert</Badge>
                </div>
                <CardTitle className="text-lg">Immobiliardarlehen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-2">Registrierungsnummer:</p>
                <p className="font-mono text-sm bg-accent/10 p-2 rounded break-all">D-W-155-BT4W-12</p>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6 bg-accent/5 border-accent/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                <div className="space-y-2 text-sm">
                  <p className="font-semibold">Zuständige Behörde:</p>
                  <p>IHK für München und Oberbayern</p>
                  <p>Max-Joseph-Straße 2, 80333 München</p>
                  <p>Telefon: +49 89 5116-0</p>
                  <p>
                    <a href="https://www.ihk-muenchen.de" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      www.ihk-muenchen.de
                    </a>
                  </p>
                  <div className="mt-4 pt-4 border-t border-accent/20">
                    <p className="font-semibold mb-2">Vermittlerregister-Prüfung:</p>
                    <p>Deutsche Industrie- und Handelskammer (DIHK)</p>
                    <p>Breite Straße 29, 10178 Berlin</p>
                    <p>Telefon: +49 180 6005850</p>
                    <p>
                      <a href="https://www.vermittlerregister.info" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                        www.vermittlerregister.info
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </ResponsiveSection>

        {/* Tätigkeitsbereich Section */}
        <ResponsiveSection spacing="lg">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Tätigkeitsbereich</h2>
            <p className="text-muted-foreground">Detaillierte Informationen zu meinen Tätigkeitsbereichen</p>
          </div>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            <AccordionItem value="item-1" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">§ 34d GewO – Versicherungsmakler</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4">
                <p className="leading-relaxed">
                  Ich bin als Versicherungsmakler mit Erlaubnis nach 34d Abs. 1 GewO tätig und biete im Rahmen der Vermittlung auch entsprechende Beratung an. Für die Vermittlung und Beratung wird von meinen Kunden keine gesonderte Vergütung verlangt. Ich erhalte ausschließlich Provisionen durch unsere Produktpartner, welche die Vermittlungskosten innerhalb der Prämie berücksichtigen und keine weiteren Zuwendungen.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">§ 34f GewO – Finanzanlagenvermittler</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4">
                <div className="space-y-3 leading-relaxed">
                  <p>
                    Ich bin für den Bereich Investmentfonds mit Erlaubnis nach § 34f Abs. 1 Satz 1 Nr. 1 GewO als Finanzanlagenvermittler gemäß Ausnahmeregelung § 2 Abs. 6 Satz 1 Nr. 8 KWG tätig. Hierbei stehen mir zusätzlich die in der Anlage 1 unter "Investmentgesellschaften" genannten Produktpartner für meine Beratung zur Verfügung.
                  </p>
                  <p>
                    Keinerlei Beratung führe ich bei erlaubnispflichtigen Produkten wie z.B. Zertifikaten, Aktien, Derivaten, geschlossenen Fonds und sonstigen Vermögensanlagen im Sinne des § 1 Abs. 2 VermAnIG sowie Tätigkeiten und Empfehlungen, die eine Erlaubnis nach § 32 KWG erfordern, durch.
                  </p>
                  <p>
                    Für die Vermittlung und Betreuung wird von meinen Kunden keine gesonderte Vergütung verlangt. Ich erhalte ausschließlich Provisionen aus Abschlusskosten, Ausgabeaufschlägen, Verwaltungsvergütungen, sowie Service- und Transaktionsgebühren, ggf. von den Produktpartnern, die bis zu 100% dieser Gebühren betragen können. Sollte ich im Einzelfall eine Gebühr von meinen Kunden erhalten, so geschieht dies nur, wenn und soweit im Vorhinein gesondert mit Ihnen vereinbart.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">§ 34i GewO – Immobiliardarlehensvermittler</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4">
                <p className="leading-relaxed">
                  Ich bin als Immobiliardarlehensvermittler nach § 34i Abs. 1 GewO tätig und im Vermittlerregister für Immobiliardarlehen unter der Nummer D-W-155-BT4W-12 eingetragen.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">Weitere Produktbereiche</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4">
                <p className="leading-relaxed">
                  Hier bin ich als ungebundener Vermittler tätig. Dadurch kann ich Ihnen eine große Auswahl an leistungsfähigen Lösungen bieten. Dabei stehen mir die in der Anlage 1 (exkl. "Investmentgesellschaften") genannten Produktpartner als Markt- und Informationsgrundlage zur Verfügung.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </ResponsiveSection>

        {/* Vergütung & Transparenz Section */}
        <ResponsiveSection variant="accent" spacing="lg" containerSize="md">
          <Card className="border-accent/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-accent" />
                Vergütung & Transparenz
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
              <div className="bg-accent/5 p-4 rounded-lg">
                <p className="font-semibold text-foreground mb-2">Keine Kosten für Sie als Kunde</p>
                <p>
                  Für die Vermittlung und Beratung wird von meinen Kunden keine gesonderte Vergütung verlangt.
                </p>
              </div>
              <div>
                <p className="font-semibold text-foreground mb-2">Provisionen von Produktpartnern</p>
                <p>
                  Ich erhalte ausschließlich Provisionen durch unsere Produktpartner, welche die Vermittlungskosten innerhalb der Prämie bzw. der Produktkosten berücksichtigen.
                </p>
              </div>
              <div className="bg-accent/5 p-4 rounded-lg">
                <p className="font-semibold text-foreground mb-2">Vollständige Transparenz</p>
                <p>
                  Alle Vergütungsdetails werden Ihnen im Rahmen der Beratung offengelegt. Ihre Interessen stehen im Mittelpunkt meiner Tätigkeit.
                </p>
              </div>
            </CardContent>
          </Card>
        </ResponsiveSection>

        {/* Offenlegung Beteiligungen Section */}
        <ResponsiveSection spacing="md" containerSize="md">
          <Card className="border-accent/20 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg">Offenlegung von Beteiligungen</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Wir besitzen weder direkte noch indirekte Beteiligungen von über 10% an den Stimmrechten oder am Kapital eines Versicherungsunternehmens, noch besitzen Versicherungsunternehmen oder Mutterunternehmen von Versicherungsunternehmen eine direkte oder indirekte Beteiligung von über 10% an den Stimmrechten oder an unserem Kapital.
              </p>
            </CardContent>
          </Card>
        </ResponsiveSection>

        {/* Schlichtungsstellen Section */}
        <ResponsiveSection variant="card" spacing="lg">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Schlichtungsstellen</h2>
            <p className="text-muted-foreground">Außergerichtliche Streitbeilegung</p>
          </div>
          
          <p className="text-center text-muted-foreground mb-8 max-w-3xl mx-auto">
            Sollte es jemals zu Streitigkeiten in Bezug auf die Vermittlung von Versicherungsverträgen kommen, stehen Ihnen außergerichtliche Schlichtungsstellen zur Verfügung:
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            <Card className="border-accent/20">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Scale className="w-8 h-8 text-accent" />
                  <CardTitle className="text-lg">Versicherungsombudsmann e.V.</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>Postfach 080632</p>
                <p>10006 Berlin</p>
                <p className="pt-2">
                  <span className="text-muted-foreground">Telefon:</span>{" "}
                  <a href="tel:+498003696000" className="text-accent hover:underline">
                    +49 800 3696000
                  </a>
                </p>
                <p>
                  <span className="text-muted-foreground">Telefax:</span> +49 800 3699000
                </p>
                <p className="pt-2">
                  <a 
                    href="https://www.versicherungsombudsmann.de" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-accent hover:underline"
                  >
                    www.versicherungsombudsmann.de
                  </a>
                </p>
              </CardContent>
            </Card>

            <Card className="border-accent/20">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Scale className="w-8 h-8 text-accent" />
                  <CardTitle className="text-lg break-words">Ombudsmann Private KV und PV</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>Postfach 060222</p>
                <p>10052 Berlin</p>
                <p className="pt-2">
                  <span className="text-muted-foreground">Telefon:</span>{" "}
                  <a href="tel:+498002550444" className="text-accent hover:underline">
                    +49 800 2550444
                  </a>
                </p>
                <p>
                  <span className="text-muted-foreground">Telefax:</span> +49 30 20458931
                </p>
                <p className="pt-2">
                  <a 
                    href="https://www.pkv-ombudsmann.de" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-accent hover:underline"
                  >
                    www.pkv-ombudsmann.de
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </ResponsiveSection>

        {/* Datenschutz Section */}
        <ResponsiveSection spacing="lg" containerSize="md">
          <Card className="border-accent/20 bg-accent/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-6 h-6 text-accent" />
                Datenschutz
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>
                Der Schutz Ihrer persönlichen Daten ist uns sehr wichtig. Wir verarbeiten Ihre Daten ausschließlich im Rahmen der gesetzlichen Bestimmungen (DSGVO, BDSG).
              </p>
              <p>
                Detaillierte Informationen zur Datenverarbeitung finden Sie in unserer{" "}
                <Link 
                  to="/datenschutz" 
                  className="text-accent hover:underline font-medium"
                >
                  Datenschutzerklärung
                </Link>
                .
              </p>
            </CardContent>
          </Card>
        </ResponsiveSection>

        {/* Download Section */}
        <ResponsiveSection variant="accent" spacing="lg" containerSize="md">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Dokument herunterladen</h2>
            <p className="text-muted-foreground mb-6">
              Sie können die Erstinformation auch als PDF-Dokument herunterladen
            </p>
            <Button 
              size="lg" 
              className="gap-2"
              asChild
            >
              <a href="/erstinformation.pdf" download="Erstinformation_Praxis-Versichert.pdf">
                <Download className="w-5 h-5" />
                Erstinformation als PDF herunterladen
              </a>
            </Button>
            <p className="text-muted-foreground mt-6 text-sm">
              Weitere Informationen finden Sie auch im{" "}
              <Link to="/impressum" className="text-accent hover:text-accent/80 transition-colors underline font-medium">
                Impressum
              </Link>
            </p>
          </div>
        </ResponsiveSection>
      </main>

      <Footer />
      </div>
    </>
  );
};

export default Erstinformation;
